/**
 * MT Tapex Blog — Interactive Logic
 * - Category tab filtering (tabs bar + sidebar cats)
 * - Search filtering
 * - Pagination (client-side, per-page = 12)
 * - Tabs scroll arrow
 */

(function () {
  'use strict';

  const PER_PAGE = 12;        // 4 rows × 3 cols
  const SUBSCRIBE_AFTER = 6;  // insert subscribe block after 6th card (row 2)

  class TapexBlog {
    constructor(root) {
      this.root = root;
      this.activeCategory = 'all';
      this.searchQuery = '';
      this.currentPage = 1;

      // Elements
      this.tabs = Array.from(root.querySelectorAll('.mt-tapex-blog__tab'));
      this.catBtns = Array.from(root.querySelectorAll('.mt-tapex-blog__cat-btn'));
      this.cards = Array.from(root.querySelectorAll('.mt-tapex-blog__card'));
      this.searchInput = root.querySelector('.mt-tapex-blog__search-input');
      this.paginationWrap = root.querySelector('.mt-tapex-blog__pagination');
      this.grid = root.querySelector('.mt-tapex-blog__grid');
      this.subscribeWrap = root.querySelector('.mt-tapex-blog__subscribe-wrap');
      // Elements — 更新为双箭头
      this.tabsInner = root.querySelector('.mt-tapex-blog__tabs-inner');
      this.arrowPrev = root.querySelector('.mt-tapex-blog__tabs-arrow--prev');
      this.arrowRight = root.querySelector('.mt-tapex-blog__tabs-arrow--next'); // keep alias

      this._bindEvents();
      this._render();
    }

    /* ---- Events ---- */
    _bindEvents() {
      // Tabs
      this.tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          this.activeCategory = tab.dataset.category;
          this.currentPage = 1;
          this._syncActiveTab();
          this._render();
        });
      });

      // Sidebar category buttons
      this.catBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          this.activeCategory = btn.dataset.category;
          this.currentPage = 1;
          this._syncActiveTab();
          this._render();
        });
      });

      // Search
      if (this.searchInput) {
        this.searchInput.addEventListener('input', () => {
          this.searchQuery = this.searchInput.value.trim().toLowerCase();
          this.currentPage = 1;
          this._render();
        });
      }

      // Subscribe form — direct POST to Shopify /contact (no AJAX, let Shopify handle redirect)
      const subscribeForm = this.root.querySelector('.mt-tapex-blog__subscribe');
      if (subscribeForm) {
        subscribeForm.addEventListener('submit', (e) => {
          const msgEl = subscribeForm.querySelector('.mt-tapex-blog__subscribe-msg');
          const consentBox = subscribeForm.querySelector('.mt-tapex-blog__consent-checkbox');
          const emailField = subscribeForm.querySelector('input[type="email"]');

          // Validate consent
          if (consentBox && !consentBox.checked) {
            e.preventDefault();
            this._showSubscribeMsg(msgEl, 'Please accept the privacy policy first.', false);
            return;
          }

          // Validate email
          if (!emailField || !emailField.value.trim()) {
            e.preventDefault();
            this._showSubscribeMsg(msgEl, 'Please enter your email address.', false);
            return;
          }

          // All good — allow native form submission to Shopify
          const btn = subscribeForm.querySelector('.mt-tapex-blog__subscribe-btn');
          if (btn) {
            btn.disabled = true;
            btn.textContent = 'Submitting…';
          }
        });

        // Show success message if redirected back with ?contact_posted=true
        if (window.location.search.includes('contact_posted=true')) {
          const msgEl = subscribeForm.querySelector('.mt-tapex-blog__subscribe-msg');
          this._showSubscribeMsg(msgEl, 'Thank you! Your message has been sent successfully.', true);
          subscribeForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }

      // Tabs 双箭头：按滚动位置显隐
      if (this.tabsInner) {
        const updateArrows = () => {
          const overflowing = this.tabsInner.scrollWidth > this.tabsInner.clientWidth + 2;
          const atStart = this.tabsInner.scrollLeft <= 2;
          const atEnd = this.tabsInner.scrollLeft + this.tabsInner.clientWidth >= this.tabsInner.scrollWidth - 2;

          if (this.arrowPrev) this.arrowPrev.style.display = (!overflowing || atStart) ? 'none' : 'flex';
          if (this.arrowRight) this.arrowRight.style.display = (!overflowing || atEnd) ? 'none' : 'flex';
        };

        this._updateTabArrows = updateArrows;
        updateArrows();
        this.tabsInner.addEventListener('scroll', updateArrows, { passive: true });
        window.addEventListener('resize', updateArrows);

        if (this.arrowPrev) {
          this.arrowPrev.addEventListener('click', () => {
            this.tabsInner.scrollBy({ left: -200, behavior: 'smooth' });
          });
        }
        if (this.arrowRight) {
          this.arrowRight.addEventListener('click', () => {
            this.tabsInner.scrollBy({ left: 200, behavior: 'smooth' });
          });
        }
      }
    }

    /* ---- Subscribe message helper ---- */
    _showSubscribeMsg(el, text, success) {
      if (!el) return;
      el.textContent = text;
      el.style.display = 'block';
      el.style.color = success ? '#4caf50' : '#e03030';
      el.style.marginTop = '8px';
      el.style.fontSize = '13px';
      clearTimeout(this._msgTimer);
      if (success) {
        this._msgTimer = setTimeout(() => { el.style.display = 'none'; }, 5000);
      }
    }

    /* ---- Sync active state ---- */
    _syncActiveTab() {
      this.tabs.forEach(t => t.classList.toggle('is-active', t.dataset.category === this.activeCategory));
      this.catBtns.forEach(b => b.classList.toggle('is-active', b.dataset.category === this.activeCategory));
    }

    /* ---- Filter logic ---- */
    _getFilteredCards() {
      const filtered = this.cards.filter(card => {
        const catMatch =
          this.activeCategory === 'all' ||
          (card.dataset.category || '').toLowerCase() === this.activeCategory.toLowerCase();

        const searchMatch =
          !this.searchQuery ||
          card.dataset.title.toLowerCase().includes(this.searchQuery) ||
          (card.dataset.excerpt || '').toLowerCase().includes(this.searchQuery);

        return catMatch && searchMatch;
      });

      // All tab: merge every category, sort by publish date (newest first)
      if (this.activeCategory === 'all') {
        return filtered.slice().sort((a, b) => {
          const dateA = parseInt(a.dataset.published || '0', 10);
          const dateB = parseInt(b.dataset.published || '0', 10);
          return dateB - dateA;
        });
      }

      return filtered;
    }

    /* ---- Render ---- */
    _render() {
      const filtered = this._getFilteredCards();
      const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
      if (this.currentPage > totalPages) this.currentPage = totalPages;

      const start = (this.currentPage - 1) * PER_PAGE;
      const end = start + PER_PAGE;
      const pageCards = filtered.slice(start, end);

      // Show/hide all article cards
      this.cards.forEach(card => card.classList.add('is-hidden'));
      pageCards.forEach(card => card.classList.remove('is-hidden'));

      // Re-order DOM inside grid:
      // 1. append visible cards in order to the grid
      // 2. insert subscribe-wrap after the 6th visible card (= end of row 2)
      if (this.grid && this.subscribeWrap) {
        pageCards.forEach(card => this.grid.appendChild(card));

        const insertAfterIndex = SUBSCRIBE_AFTER - 1; // 0-based index of 6th card = 5
        if (pageCards.length > insertAfterIndex) {
          const anchorCard = pageCards[insertAfterIndex];
          // insertBefore the node AFTER anchorCard, or appendChild if it's the last
          const nextNode = anchorCard.nextSibling;
          if (nextNode && nextNode !== this.subscribeWrap) {
            this.grid.insertBefore(this.subscribeWrap, nextNode);
          } else if (!nextNode) {
            this.grid.appendChild(this.subscribeWrap);
          }
        } else {
          // Fewer than 6 visible cards — put subscribe at the very end
          this.grid.appendChild(this.subscribeWrap);
        }
        this.subscribeWrap.style.display = '';
      }

      // Render pagination
      this._renderPagination(totalPages);
    }

    /* ---- Pagination ---- */
    _renderPagination(totalPages) {
      if (!this.paginationWrap) return;
      this.paginationWrap.innerHTML = '';
      if (totalPages <= 1) return;

      const cur = this.currentPage;

      // Prev arrow
      const prev = this._makePaginationArrow('prev', cur <= 1);
      this.paginationWrap.appendChild(prev);

      // Page numbers with ellipsis
      const pages = this._getPageNumbers(cur, totalPages);
      pages.forEach(p => {
        if (p === '...') {
          const dots = document.createElement('span');
          dots.className = 'mt-tapex-blog__pagination-dots';
          dots.textContent = '...';
          this.paginationWrap.appendChild(dots);
        } else {
          const btn = document.createElement('button');
          btn.className = 'mt-tapex-blog__pagination-btn' + (p === cur ? ' is-active' : '');
          btn.textContent = p;
          btn.addEventListener('click', () => {
            this.currentPage = p;
            this._render();
            this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
          this.paginationWrap.appendChild(btn);
        }
      });

      // Next arrow
      const next = this._makePaginationArrow('next', cur >= totalPages);
      this.paginationWrap.appendChild(next);
    }

    _makePaginationArrow(dir, disabled) {
      const btn = document.createElement('button');
      btn.className = 'mt-tapex-blog__pagination-arrow' + (disabled ? ' is-hidden' : '');
      btn.innerHTML =
        dir === 'prev'
          ? '<svg t="1775108310294" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="21550" width="18" height="18"><path d="M927.8 478H175.5l219.3-219.3c10.7-10.7 10.7-28.1 0-38.7l-6.5-6.5c-10.7-10.7-28.1-10.7-38.7 0L81.7 481.3c-9.2 4.5-16 13.4-17.6 23.9-1.5 7.1-0.1 14.7 4 21 2.4 4 5.6 7.5 9.4 10.1l0.5 0.5c2.6 2.6 5.6 4.6 8.8 5.9l266.7 266.7c10.7 10.7 28.1 10.7 38.7 0l6.5-6.5c10.7-10.7 10.7-28.1 0-38.7l-222-222.3h751.1c17.6 0 32-14.4 32-32 0-17.5-14.4-31.9-32-31.9z" fill="#666666" p-id="21551"></path></svg>'
          : '<svg t="1775107916320" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="15533" width="18" height="18"><path d="M95.5 478h752.3L628.5 258.7c-10.7-10.7-10.7-28.1 0-38.7l6.5-6.5c10.7-10.7 28.1-10.7 38.7 0l267.9 267.9c9.2 4.5 16 13.4 17.6 23.9 1.5 7.1 0.1 14.7-4 21-2.4 4-5.6 7.5-9.4 10.1l-0.5 0.5c-2.6 2.6-5.6 4.6-8.8 5.9L669.7 809.3c-10.7 10.7-28.1 10.7-38.7 0l-6.5-6.5c-10.7-10.7-10.7-28.1 0-38.7L846.6 542H95.5c-17.6 0-32-14.4-32-32s14.4-32 32-32z" fill="#666666" p-id="15534"></path></svg>';
      btn.addEventListener('click', () => {
        if (dir === 'prev') this.currentPage -= 1;
        else this.currentPage += 1;
        this._render();
        this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      return btn;
    }

    _getPageNumbers(cur, total) {
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
      const pages = [];
      pages.push(1);
      if (cur > 3) pages.push('...');
      for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++) {
        pages.push(i);
      }
      if (cur < total - 2) pages.push('...');
      pages.push(total);
      return pages;
    }
  }

  // Init
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.mt-tapex-blog').forEach(el => new TapexBlog(el));
  });
})();
